import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { SearchableSelect } from '../../components/ui/SearchableSelect';
import { ApiError } from '../../lib/api-client';
import { confirmUpdate } from '../../lib/confirm-service';
import { normalizePhoneInput, PHONE_INPUT_MAX_LENGTH, PHONE_INPUT_PATTERN, PHONE_INPUT_TITLE } from '../../lib/phone-input';
import { useToast } from '../../lib/toast';
import { patientFormSchema, type PatientFormValues } from './patient.schema';
import { useCreatePatient, usePatientLookup, useUpdatePatient } from './patient.queries';
import { bloodGroups, patientGenders, type Patient, type PatientPayload } from './types';

interface PatientFormProps {
  patient?: Patient | null;
  onCancel: () => void;
  onSaved: () => void;
}

const readableLabel = (value: string) => value
  .split('-')
  .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
  .join(' ');

const genderOptions = patientGenders.map((value) => ({ value, label: readableLabel(value) }));
const bloodGroupOptions = bloodGroups.map((value) => ({ value, label: value === 'unknown' ? 'Unknown' : value }));

export const PatientForm = ({ patient, onCancel, onSaved }: PatientFormProps) => {
  const isEdit = Boolean(patient);
  const { showToast } = useToast();
  const createMutation = useCreatePatient();
  const updateMutation = useUpdatePatient();
  const activeMutation = isEdit ? updateMutation : createMutation;
  const {
    register,
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<PatientFormValues>({
    resolver: zodResolver(patientFormSchema),
    defaultValues: patient ? {
      medicalRecordNumber: patient.medicalRecordNumber,
      firstName: patient.firstName,
      lastName: patient.lastName,
      dateOfBirth: patient.dateOfBirth.slice(0, 10),
      gender: patient.gender,
      phone: normalizePhoneInput(patient.phone),
      email: patient.email ?? '',
      bloodGroup: patient.bloodGroup,
      address: patient.address ?? '',
      state: patient.state ?? '',
      district: patient.district ?? '',
      pinCode: patient.pinCode ?? '',
    } : {
      medicalRecordNumber: '',
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      gender: 'prefer-not-to-say',
      phone: '',
      email: '',
      bloodGroup: 'unknown',
      address: '',
      state: '',
      district: '',
      pinCode: '',
    },
  });
  const phoneField = register('phone');

  const selectedState = watch('state');
  const states = usePatientLookup('states');
  const districts = usePatientLookup('districts', { state: selectedState ?? '' });

  const [fullName, setFullName] = useState(() => {
    if (!patient) return '';
    return patient.firstName === patient.lastName ? patient.firstName : `${patient.firstName} ${patient.lastName}`.trim();
  });

  const applyFullName = (value: string) => {
    setFullName(value);
    const [firstWord, ...rest] = value.trim().replace(/\s+/g, ' ').split(' ').filter(Boolean);
    setValue('firstName', firstWord ?? '', { shouldValidate: true });
    setValue('lastName', rest.length > 0 ? rest.join(' ') : (firstWord ?? ''), { shouldValidate: true });
  };

  const submitPatient = handleSubmit((values) => {
    const formValues: Omit<PatientPayload, 'allergies' | 'status'> = {
      medicalRecordNumber: values.medicalRecordNumber.toUpperCase(),
      firstName: values.firstName,
      lastName: values.lastName,
      dateOfBirth: values.dateOfBirth,
      gender: values.gender,
      phone: values.phone,
      bloodGroup: values.bloodGroup,
      address: values.address,
      ...(values.email ? { email: values.email } : {}),
      ...(values.state ? { state: values.state } : {}),
      ...(values.district ? { district: values.district } : {}),
      ...(values.pinCode ? { pinCode: values.pinCode } : {}),
    };
    const onError = (error: unknown) =>
      showToast(error instanceof ApiError ? error.message : `The patient could not be ${isEdit ? 'updated' : 'saved'}.`, 'error');

    if (isEdit && patient) {
      void confirmUpdate(`the patient record (${values.medicalRecordNumber.toUpperCase()})`).then((confirmed) => {
        if (!confirmed) return;
        updateMutation.mutate({ patientId: patient.id, payload: formValues }, {
          onSuccess: () => {
            showToast('Patient updated successfully.', 'success');
            onSaved();
          },
          onError,
        });
      });
    } else {
      const payload: PatientPayload = { ...formValues, allergies: [], status: 'active' };
      createMutation.mutate(payload, {
        onSuccess: () => {
          showToast('Patient registered successfully.', 'success');
          onSaved();
        },
        onError,
      });
    }
  });

  return (
    <section className="form-card compact-form" aria-labelledby="patient-form-title">
      <div className="section-heading">
        <div><p className="eyebrow">{isEdit ? 'Edit record' : 'New record'}</p><h2 id="patient-form-title">{isEdit ? 'Edit patient' : 'Register a patient'}</h2></div>
        <button className="icon-button" type="button" onClick={onCancel} aria-label="Close form">×</button>
      </div>

      <form onSubmit={(event) => void submitPatient(event)}>
        <div className="form-grid">
          <label className="field field-sm">
            <span>UHID</span>
            <input {...register('medicalRecordNumber')} readOnly autoComplete="off" />
            {errors.medicalRecordNumber ? <small>{errors.medicalRecordNumber.message}</small> : null}
          </label>
          <label className="field field-lg">
            <span>Patient name</span>
            <input value={fullName} onChange={(event) => applyFullName(event.target.value)} placeholder="Patient's full name" autoComplete="name" aria-invalid={Boolean(errors.firstName || errors.lastName)} />
            {errors.firstName ?? errors.lastName ? <small>{(errors.firstName ?? errors.lastName)?.message}</small> : null}
          </label>
          <label className="field field-sm">
            <span>Phone</span>
            <input {...phoneField} type="tel" inputMode="numeric" pattern={PHONE_INPUT_PATTERN} maxLength={PHONE_INPUT_MAX_LENGTH} title={PHONE_INPUT_TITLE} placeholder="9876543210" autoComplete="tel" aria-invalid={Boolean(errors.phone)} onChange={(event) => { event.target.value = normalizePhoneInput(event.target.value); void phoneField.onChange(event); }} />
            {errors.phone ? <small>{errors.phone.message}</small> : null}
          </label>
          <label className="field field-sm">
            <span>Gender</span>
            <Controller
              control={control}
              name="gender"
              render={({ field }) => (
                <SearchableSelect
                  options={genderOptions}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  ariaInvalid={Boolean(errors.gender)}
                />
              )}
            />
            {errors.gender ? <small>{errors.gender.message}</small> : null}
          </label>
          <label className="field field-sm">
            <span>Date of birth</span>
            <input {...register('dateOfBirth')} type="date" max={new Date().toISOString().slice(0, 10)} aria-invalid={Boolean(errors.dateOfBirth)} />
            {errors.dateOfBirth ? <small>{errors.dateOfBirth.message}</small> : null}
          </label>
          <label className="field field-sm">
            <span>Blood group</span>
            <Controller
              control={control}
              name="bloodGroup"
              render={({ field }) => (
                <SearchableSelect
                  options={bloodGroupOptions}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  ariaInvalid={Boolean(errors.bloodGroup)}
                />
              )}
            />
            {errors.bloodGroup ? <small>{errors.bloodGroup.message}</small> : null}
          </label>
          <label className="field">
            <span>Email <em>optional</em></span>
            <input {...register('email')} type="email" placeholder="patient@example.com" autoComplete="email" aria-invalid={Boolean(errors.email)} />
            {errors.email ? <small>{errors.email.message}</small> : null}
          </label>
          <div className="form-grid field-full">
            <label className="field field-lg">
              <span>Address</span>
              <input {...register('address')} placeholder="House no, street, locality" autoComplete="street-address" aria-invalid={Boolean(errors.address)} />
              {errors.address ? <small>{errors.address.message}</small> : null}
            </label>
            <label className="field field-sm">
              <span>State</span>
              <Controller
                control={control}
                name="state"
                render={({ field }) => (
                  <SearchableSelect
                    id="patient-state"
                    value={field.value ?? ''}
                    options={(states.data ?? []).map((item) => ({ value: item.label, label: item.label }))}
                    placeholder="Select state"
                    onChange={(value) => { field.onChange(value); setValue('district', ''); }}
                    onBlur={field.onBlur}
                    ariaInvalid={Boolean(errors.state)}
                  />
                )}
              />
              {errors.state ? <small>{errors.state.message}</small> : null}
            </label>
            <label className="field field-sm">
              <span>District</span>
              <Controller
                control={control}
                name="district"
                render={({ field }) => (
                  <SearchableSelect
                    id="patient-district"
                    value={field.value ?? ''}
                    options={(districts.data ?? []).map((item) => ({ value: item.label, label: item.label }))}
                    placeholder="Select district"
                    disabled={!selectedState}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    ariaInvalid={Boolean(errors.district)}
                  />
                )}
              />
              {errors.district ? <small>{errors.district.message}</small> : null}
            </label>
            <label className="field field-xs">
              <span>Pin code</span>
              <input {...register('pinCode')} placeholder="700001" autoComplete="postal-code" aria-invalid={Boolean(errors.pinCode)} />
              {errors.pinCode ? <small>{errors.pinCode.message}</small> : null}
            </label>
          </div>
        </div>
        <div className="form-actions">
          <button className="button button-secondary" type="button" onClick={onCancel}>Cancel</button>
          <button className="button button-primary" type="submit" disabled={activeMutation.isPending}>
            {activeMutation.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Register patient'}
          </button>
        </div>
      </form>
    </section>
  );
};
